<template>
  <div class="panel-sunburst">
    <p v-if="!nodes.length" class="panel-sunburst__empty">
      {{ $t('communication-status-panel.empty') }}
    </p>

    <template v-else>
      <e-chart
        ref="chart"
        class="panel-sunburst__chart"
        autoresize
        watch-shallow
        :options="option"
        :aria-label="chartLabel"
        @click="onNodeClick"
      />

      <!-- O miolo só existe quando há drill-down para desfazer: na frota
           inteira ele some e o furo fica limpo. -->
      <button
        v-if="trail.length"
        type="button"
        class="panel-sunburst__center"
        :title="$t('communication-status-panel.sunburst-back')"
        @click="goUp"
      >
        <span class="panel-sunburst__back">‹ {{ currentNode.name }}</span>
        <span class="panel-sunburst__center-detail">
          {{ $t('communication-status-panel.sunburst-assets', { count: currentNode.assetCount }) }}
        </span>
      </button>
    </template>
  </div>
</template>

<script>
import {
  buildSunburstTree,
  collectAssetNodes,
  pruneUnreadableLevels,
  sliceTree,
  sunburstLevels,
  DEFAULT_SUNBURST_ROOT,
} from '@/domain/services/generalPanel/communicationStatusPanel/panelSunburst';
import buildSunburstOption from '@/domain/services/generalPanel/communicationStatusPanel/sunburstOption';
import readPanelPalette from '@/domain/services/generalPanel/communicationStatusPanel/panelPalette';

/** Meio segundo aceso, meio apagado: pulso perceptível sem virar estroboscópio. */
const PULSE_INTERVAL_IN_MS = 700;

/**
 * Ciclos de inatividade até o painel voltar sozinho para a frota inteira.
 *
 * O Painel opera sem operador. Sem esse retorno, alguém que encosta na TV deixa
 * a tela vigiando uma Instalação só, e ninguém percebe que o resto sumiu.
 */
const AUTO_RETURN_CYCLES = 2;
const MS_IN_SECOND = 1000;

function escapeHtml(value) {
  return String(value === undefined || value === null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default {
  name: 'PanelSunburst',
  props: {
    assets: {
      type: Array,
      default: () => [],
    },
    changedKeys: {
      type: Array,
      default: () => [],
    },
    refreshIntervalInSeconds: {
      type: Number,
      default: 120,
    },
    /** Nível do anel mais interno: `company` ou `installation`. */
    rootLevel: {
      type: String,
      default: DEFAULT_SUNBURST_ROOT,
    },
  },
  data: () => ({
    /**
     * `{ width, height }` do gráfico. O corte do rótulo é medido na espessura
     * real do anel, que muda com o tamanho da tela. Nulo até a primeira medida.
     */
    chartSize: null,
    rootPath: [],
    pulseOn: false,
    pulseTimer: null,
    returnTimer: null,
  }),
  computed: {
    /**
     * Recalculada quando o tema do perfil muda. A leitura acontece aqui, e não
     * no import, porque o ECharts desenha em canvas e não enxerga variável CSS
     * — e porque `themeService` só reflete o tema novo depois de `setTheme`,
     * que roda antes do commit da store.
     */
    palette() {
      // Dependência explícita: sem ela a paleta não recalcularia na troca.
      this.$store.getters.theme; // eslint-disable-line no-unused-expressions
      return readPanelPalette();
    },
    tree() {
      return buildSunburstTree(this.assets, this.rootLevel);
    },
    slice() {
      return sliceTree(this.tree, this.rootPath);
    },
    /**
     * Os anéis efetivamente desenhados. A profundidade acompanha o recorte: um
     * anel só entra quando seus setores são grandes o bastante para se ler.
     */
    nodes() {
      return pruneUnreadableLevels(this.slice.nodes);
    },
    trail() {
      return this.slice.trail;
    },
    currentNode() {
      return this.trail[this.trail.length - 1] || null;
    },
    option() {
      return buildSunburstOption({
        nodes: this.nodes,
        palette: this.palette,
        formatTooltip: this.formatTooltip,
        size: this.chartSize,
      });
    },
    /** Chaves de Ativo que mudaram neste ciclo e estão visíveis no recorte. */
    pulsingIds() {
      const changed = this.changedKeys || [];
      return collectAssetNodes(this.nodes)
        .filter(node => changed.indexOf(node.key) >= 0)
        .map(node => node.id);
    },
    /** O canvas é opaco para leitores de tela; a descrição fica no elemento. */
    chartLabel() {
      const scope = this.currentNode
        ? this.currentNode.name
        : this.$t('communication-status-panel.sunburst-fleet');

      return `${this.$t('communication-status-panel.views.radial')} — ${scope}`;
    },
  },
  watch: {
    pulsingIds: 'restartPulse',
    rootPath: 'restartAutoReturn',
    // Trocar a raiz refaz a árvore inteira: o caminho anterior aponta para nós
    // que não existem mais nela.
    rootLevel() {
      this.rootPath = [];
    },
  },
  mounted() {
    this.restartPulse();
    this.measureChart();
    this.startSizeWatch();
  },
  beforeDestroy() {
    this.stopPulse();
    this.stopAutoReturn();
    this.stopSizeWatch();
  },
  methods: {
    /**
     * Só desce em nó que tem filhos. Ativo é folha: clicar nele não faz nada,
     * porque o anel de dentro dele seria o Sensor, que a tela não desenha.
     */
    onNodeClick(params) {
      const node = params && params.data && params.data.node;

      if (!node || !node.children.length) {
        return;
      }

      this.rootPath = this.rootPath.concat([node.key]);
    },
    goUp() {
      this.rootPath = this.rootPath.slice(0, -1);
    },
    formatTooltip(node) {
      if (!node) {
        return '';
      }

      const title = `<strong>${escapeHtml(node.name)}</strong>`;

      return [title].concat(this.describe(node)).join('<br>');
    },
    describe(node) {
      if (node.level === sunburstLevels.SENSOR) {
        return this.describeSensor(node);
      }

      return node.level === sunburstLevels.ASSET
        ? this.describeAsset(node)
        : this.describeGroup(node);
    },
    /** O Sensor fala o vocabulário de oito valores do Status de Módulo. */
    describeSensor(node) {
      const status = this.$t(`enums.module-status.${node.status}`);

      return [
        escapeHtml(`${node.installation} · ${node.asset}`),
        escapeHtml(status),
      ];
    },
    describeAsset(node) {
      const state = this.$t(`communication-status-panel.state.${node.state}`);
      const sensors = this.$t(
        'communication-status-panel.sunburst-sensors',
        { count: node.sensorCount },
      );

      return [
        escapeHtml(`${node.company} · ${node.installation}`),
        escapeHtml(`${state} · ${sensors}`),
      ];
    },
    describeGroup(node) {
      const counts = [
        this.$t('communication-status-panel.sunburst-assets', { count: node.assetCount }),
        this.$t('communication-status-panel.sunburst-sensors', { count: node.sensorCount }),
      ].join(' · ');

      const load = node.attentionLoad === null
        ? this.$t('communication-status-panel.sunburst-load-empty')
        : this.$t('communication-status-panel.sunburst-load', {
          percent: Math.round(node.attentionLoad * 100),
        });

      return [escapeHtml(counts), escapeHtml(load)];
    },
    /**
     * Pulsação de Alarme no canvas.
     *
     * CSS não alcança o interior de um canvas, então o pulso alterna o estado
     * de ênfase do próprio ECharts nos setores que mudaram de estado neste
     * ciclo. Para sozinho no ciclo seguinte, quando `changedKeys` é recalculado.
     */
    restartPulse() {
      this.stopPulse();

      if (!this.pulsingIds.length) {
        return;
      }

      this.pulseTimer = setInterval(() => {
        this.pulseOn = !this.pulseOn;
        this.applyPulse(this.pulseOn);
      }, PULSE_INTERVAL_IN_MS);
    },
    applyPulse(on) {
      const { chart } = this.$refs;

      if (!chart) {
        return;
      }

      this.pulsingIds.forEach(id => chart.dispatchAction({
        type: on ? 'sunburstHighlight' : 'sunburstUnhighlight',
        seriesIndex: 0,
        targetNodeId: id,
      }));
    },
    stopPulse() {
      if (this.pulseTimer) {
        clearInterval(this.pulseTimer);
        this.pulseTimer = null;
      }

      if (this.pulseOn) {
        this.pulseOn = false;
        this.applyPulse(false);
      }
    },
    restartAutoReturn() {
      this.stopAutoReturn();

      if (!this.rootPath.length) {
        return;
      }

      this.returnTimer = setTimeout(
        () => { this.rootPath = []; },
        AUTO_RETURN_CYCLES * this.refreshIntervalInSeconds * MS_IN_SECOND,
      );
    },
    stopAutoReturn() {
      if (this.returnTimer) {
        clearTimeout(this.returnTimer);
        this.returnTimer = null;
      }
    },
    /**
     * Mede o gráfico. O elemento raiz ocupa o mesmo espaço que o canvas, e
     * existe mesmo quando não há dado para desenhar.
     */
    measureChart() {
      const width = this.$el ? this.$el.clientWidth : 0;
      const height = this.$el ? this.$el.clientHeight : 0;
      const current = this.chartSize;

      if (!width || !height) {
        return;
      }

      if (current && current.width === width && current.height === height) {
        return;
      }

      this.chartSize = { width, height };
    },
    /**
     * O gráfico muda de tamanho sem a janela mudar — o menu lateral abre, o
     * Placar passa para duas faixas —, por isso o elemento é observado. Onde não
     * há `ResizeObserver` (TV com navegador antigo), o resize da janela cobre o
     * caso mais comum.
     */
    startSizeWatch() {
      if (typeof window.ResizeObserver === 'function') {
        this.sizeObserver = new window.ResizeObserver(() => this.measureChart());
        this.sizeObserver.observe(this.$el);
        return;
      }

      window.addEventListener('resize', this.measureChart);
    },
    stopSizeWatch() {
      if (this.sizeObserver) {
        this.sizeObserver.disconnect();
        this.sizeObserver = null;
        return;
      }

      window.removeEventListener('resize', this.measureChart);
    },
  },
};
</script>

<style lang="sass" scoped>

.panel-sunburst
  align-items: center
  display: flex
  height: 100%
  justify-content: center
  position: relative
  // O corpo do painel vira flex row no modo Sunburst, e este e o seu item. Sem
  // largura propria, a base do item seria a largura do conteudo — e o conteudo
  // e o grafico, que pede 100% do item. A referencia circular resolve em zero, o
  // ECharts inicializa num elemento de largura 0 e nao desenha nada. min-width
  // desfaz o piso automatico do flex, que impediria o encolhimento correto.
  flex: 1
  min-height: 0
  min-width: 0

  &__chart
    height: 100%
    width: 100%

  // Fica sobre o furo central do gráfico. `position: absolute` em vez de um
  // rótulo do ECharts porque é um controle, não um dado: precisa de foco de
  // teclado e de estado de hover próprios.
  &__center
    background: transparent
    border: none
    color: var(--panel-text)
    cursor: pointer
    display: flex
    flex-direction: column
    gap: 0.15rem
    left: 50%
    max-width: 18%
    padding: 0
    position: absolute
    top: 50%
    transform: translate(-50%, -50%)

    &:hover
      color: var(--panel-match)

  &__back
    font-size: 0.9rem
    font-weight: 700
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__center-detail
    color: var(--panel-text-muted)
    font-size: 0.72rem

  &__empty
    color: var(--panel-text-muted)
    font-size: 1rem
</style>
